import * as AppConstants from "AppConstants";
import axios from "axios";
import { getUrlForUserByEmail, getUrlToChangePassword, getUrlToFetchAllUsers, getUrlToFetchUsersByRole, getUrlToCreateUser, getUrlToUpdateUser, getUrlToDeleteUser } from "utils/endpointUtils/userEndpointUtil";
import { resetSessionStorage } from "./auth.service";
import { checkUserHasManagerRole, checkUserHasMemberRole, getCurrentMember, getCurrentManager, getCurrentClient, checkUserHasClientRole, checkUserHasAdminRole, readSessionIsUserLoggedIn, getLoggedInUserId, checkUserHasAccountRole } from "app/services/app.state.service";
import { handleAxiosError } from "app/services/app.error.handler";
import * as sessionStorageProxy from "app/services/app.session.storage.proxy";
import { setAuthHeader } from "./auth.header.service";
import { getClientCodeFromEmail } from "utils/clientUtil";
import { loadAllClients } from "./client.service";

// Returns true if successful, returns false otherwise
export const setUserContext = (email, token = null) => {
    return new Promise(async (resolve, reject) => {
        const url = getUrlForUserByEmail(email);

        return axios.get(url)
        .then(res => {
            var countOfRoles = res.data.roles.length;
            // Check if user has valid roles ... otherwise invalid access
            if(countOfRoles === 0) {
                resetSessionStorage();
                window.alert("User has no valid role, please contact your admin");
                reject(new Error("User has no valid role"));
                //throw new Error("User has no valid role");
            }

            sessionStorageProxy.setItem(AppConstants.SESSION_KEY_LOGGED_IN_USER, JSON.stringify(res.data))
            // for admin user, setting up below keys in Session store is not required
            if(checkUserHasAdminRole()) {
                const adminPromise = setAdminSessionPromise(token);
                resolve(Promise.resolve(adminPromise));
            }
            else if(checkUserHasAccountRole()){
                const accountPromise = setAccountSessionPromise();
                resolve(Promise.resolve(accountPromise));
            }
            else {
                const memberPromise = setMemberSessionPromise();
                const managerPromise = setManagerSessionPromise();
                const clientPromise = setClientSessionPromise();

                const promises = [memberPromise, managerPromise, clientPromise];

                // allSettled instead of any: a brand-new user with a role but no
                // Member/Manager/Client record yet should still be able to log in.
                // Profile-dependent pages handle missing data when the user
                // navigates to them.
                Promise.allSettled(promises).then((results) => {
                    const fulfilledCount = results.filter((r) => r.status === "fulfilled").length;
                    if (fulfilledCount === 0) {
                        console.warn(
                            "[setUserContext] No member/manager/client profile loaded for user; " +
                            "logging in with user record only."
                        );
                    }
                    resolve("User context set");
                });
            }
        }).catch((error) => {
            handleAxiosError(error);
            reject("User not found for setting user context: " + email);
            // reject(error);
        });
    });
}

const setMemberSessionPromise = () => {
    return new Promise((resolve, reject) => {
        if(checkUserHasMemberRole()) {
            getCurrentMember()
            .then((member) => {
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MEMBER, JSON.stringify(member));
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(member.client));
                resolve("Member and client are set in session storage");
            })
            .catch(error => {
                handleAxiosError(error);
                reject(error);
            });
        }
        else {
            reject(new Error("Insufficient privileges for user"));
        }
    });
}

const setAdminSessionPromise = (token = null) => {
    return new Promise((resolve, reject) => {
        if(checkUserHasAdminRole()) {
            loadAllClients(token)
            .then((clients) => {
                // Admin has access to all clients
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(clients.data));
                resolve("Admin set in session storage");
            })
            .catch(error => {
                handleAxiosError(error);
                reject(`Error while setting admin in context`);
            });
        }
        else {
            reject(new Error("Insufficient privileges for user"));
        }
    });
}

const setAccountSessionPromise = () => {
    return new Promise((resolve, reject) => {
        if(checkUserHasAccountRole()) {
            loadAllClients()
            .then((clients) => {
                // Admin has access to all clients
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(clients.data));
                resolve("Account set in session storage");
            })
            .catch(error => {
                handleAxiosError(error);
                reject(`Error while setting Account in context`);
            });
        }
        else {
            reject(new Error("Insufficient privileges for user"));
        }
    });
}

const setManagerSessionPromise = () => {
    return new Promise((resolve, reject) => {
        if(checkUserHasManagerRole()) {
            getCurrentManager()
            .then((managers) => {
                // A single user with Manager role, can handle multiple clients; so can have multiple manager records 
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MANAGER, JSON.stringify(managers));
                // From manager data derive clients and set into Clients session data
                var clients = managers.map( manager => {
                    return { 
                        id: manager.clientId,
                        code: getClientCodeFromEmail(manager.clientEmail),
                        contactPersonEmail: manager.clientEmail
                    }
                });
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(clients));
                resolve("Manager set in session storage");
            })
            .catch(error => {
                handleAxiosError(error);
                reject(`Error while setting manager in context`);
            });
        }
        else {
            reject(new Error("Insufficient privileges for user"));
        }
    });
}

const setClientSessionPromise = () => {
    return new Promise((resolve, reject) => {
        if(checkUserHasClientRole()) {
            getCurrentClient()
            .then((client) => {
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(client));
                resolve("Client set in session storage");
            })
            .catch(error => {
                handleAxiosError(error);
                reject(`Error while setting manager in context`);
            });
        }
        else {
            reject(new Error("Insufficient privileges for user"));
        }
    });
}

export const updatePasswordToBackend = async (passwordObj) => {
    const config = setAuthHeader();
    const user_id = getLoggedInUserId();
    
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToChangePassword(user_id);
    return await axios.put(url, passwordObj, config);
}

// Matches the server-side cap in UserController#MAX_PAGE_SIZE. We request the max page size
// and then page through the rest so the picker gets the COMPLETE user list — the cap only
// limits how many users come back per request, not the total.
const PICKER_DEFAULT_PAGE_SIZE = 200;

const buildUsersPageUrl = (pageNumber) =>
    `${getUrlToFetchAllUsers()}?pageNumber=${pageNumber}&pageSize=${PICKER_DEFAULT_PAGE_SIZE}&sortBy=email&sortDirection=ASC`;

// Used by MasterRouterFactory + AddMemberDialog/AddClientMappingDialog pickers.
// Returns an axios-shaped { data: [...users] } so existing callers that read
// `response.data` as an array continue to work. Sorted ASC by email so the picker
// dropdown reads alphabetically — independent of the paginated grid below, which
// defaults to sorting by name (fullName) ascending.
// Pages through the server-side result so tenants with more than one page of users
// (>200) still get every record, not just the first page.
export const loadAllUsersFromBackend = async () => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const firstResponse = await axios.get(buildUsersPageUrl(0), config);
    const firstPage = firstResponse.data;
    const users = [...(firstPage?.content ?? [])];

    const totalPages = firstPage?.totalPages ?? 1;
    if (totalPages > 1) {
        const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => axios.get(buildUsersPageUrl(i + 1), config))
        );
        remaining.forEach((response) => users.push(...(response.data?.content ?? [])));
    }

    return { ...firstResponse, data: users };
}

// Server-driven page fetch for the Master Data User grid. Returns the full PageResponse
// envelope (content/page/pageSize/totalElements/totalPages — see PageResponse.java) on
// response.data. The grid's default sort is name (fullName) ascending, supplied by the
// userPage slice; the 'email'/'DESC' below are only fallbacks for callers that omit sort
// params.
export const loadUsersPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const params = new URLSearchParams({
        pageNumber: String(page ?? 0),
        pageSize: String(pageSize ?? 25),
        sortBy: sortBy ?? 'email',
        sortDirection: sortDirection ?? 'DESC',
    });
    if (searchQuery) params.append('searchQuery', searchQuery);
    const url = `${getUrlToFetchAllUsers()}?${params.toString()}`;
    return await axios.get(url, config);
}

export const loadUsersByRoleFromBackend = async (role) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToFetchUsersByRole(role);
    return await axios.get(url, config);
}

export const addUserToBackend = async (data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToCreateUser();
    return await axios.post(url, data, config);
}

export const updateUserToBackend = async (user_id, data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToUpdateUser(user_id);
    return await axios.put(url, data, config);
}

export const deleteUser = async (user_id) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToDeleteUser(user_id);
    
    return await axios.delete(url, config);
}